/**
 * Wrap an element with another element
 * @param {HTMLElement|NodeList|Array} elements The element or elements to wrap
 * @param {HTMLElement|Object} wrapper The element to wrap the element with or an object with the
 *  element type and class name
 * @returns {HTMLElement}
 */
export function wrapEach(elementsToWrap, wrapper) {
    let elements = elementsToWrap;

    // Convert elementsToWrap to an array of elements if it's not already
    if (elements instanceof HTMLElement) {
        elements = [elements];
    } else if (NodeList.prototype.isPrototypeOf(elements)) {
        elements = Array.from(elements);
    }

    // Return if no elements to wrap
    if (elements.length === 0) return;

    // Wrap each element individually
    elements.forEach((element) => {
        let wrapperEl;

        // Create a new wrapper element for each item
        if (wrapper instanceof HTMLElement) {
            wrapperEl = wrapper.cloneNode(true);
        } else if (typeof wrapper === "object" && wrapper.element) {
            wrapperEl = document.createElement(wrapper.element);

            if (wrapper.className) {
                Array.isArray(wrapper.className)
                    ? wrapperEl.classList.add(...wrapper.className)
                    : wrapperEl.classList.add(wrapper.className);
            }
        }

        // If wrapperEl is a valid HTML element, proceed to wrap
        if (wrapperEl instanceof HTMLElement) {
            element.parentNode.insertBefore(wrapperEl, element);
            wrapperEl.appendChild(element);
        }
    });
}

/**
 * Wrap elements with another element
 * @param {HTMLElement|NodeList|Array} elementsToWrap The element or elements to wrap
 * @param {HTMLElement|Object} wrapper The element to wrap the elements with or an object with the
 *  element type and class name
 * @returns {HTMLElement}
 */
export function wrapAll(elementsToWrap, wrapper) {
    let wrapperEl;
    let elements = elementsToWrap;

    // Convert elementsToWrap to an array of elements
    if (elements instanceof HTMLElement) {
        elements = [elements];
    } else if (NodeList.prototype.isPrototypeOf(elements)) {
        elements = Array.from(elements);
    }

    if (elements.length === 0) return;

    // Create or use the provided wrapper element
    if (wrapper instanceof HTMLElement) {
        wrapperEl = wrapper;
    } else if (typeof wrapper === "object" && wrapper.element) {
        wrapperEl = document.createElement(wrapper.element);

        if (wrapper.className) {
            console.log(wrapper.className);

            Array.isArray(wrapper.className)
                ? wrapperEl.classList.add(...wrapper.className)
                : wrapperEl.classList.add(wrapper.className);
        }
    }

    if (!(wrapperEl instanceof HTMLElement)) {
        return;
    }

    // Get the parent node of the first element
    const parent = elements[0].parentNode;

    // Insert the wrapper before the first element
    parent.insertBefore(wrapperEl, elements[0]);

    // Append all elements to the wrapper
    elements.forEach((element) => {
        wrapperEl.appendChild(element);
    });

    return wrapperEl;
}

/**
 * Insert a new element before a reference element
 * @param {HTMLElement} referenceElement The existing element before which the new element will be inserted
 * @param {HTMLElement|Object} newElement The element to be inserted or an object with the
 *  element type and class name
 * @returns {HTMLElement|null} The inserted element, or null if the insertion failed
 */
export function insertElementBefore(referenceElement, newElement) {
    let newEl;

    if (newElement instanceof HTMLElement) {
        newEl = newElement;
    } else if (typeof newElement === "object" && newElement.element) {
        newEl = document.createElement(newElement.element);

        if (newElement.className) {
            newEl.classList.add(newElement.className);
        }
    }

    if (!(newEl instanceof HTMLElement)) {
        return null;
    }

    const parentElement = referenceElement.parentNode;

    if (parentElement) {
        parentElement.insertBefore(newEl, referenceElement);
    } else {
        return null;
    }

    return newEl;
}

/**
 * Insert a new element after a reference element
 * @param {HTMLElement} referenceElement The existing element after which the new element will be inserted
 * @param {HTMLElement|Object} newElement The element to be inserted or an object with the
 *  element type and class name
 * @returns {HTMLElement|null} The inserted element, or null if the insertion failed
 */
export function insertElementAfter(referenceElement, newElement) {
    let newEl;

    if (newElement instanceof HTMLElement) {
        newEl = newElement;
    } else if (typeof newElement === "object" && newElement.element) {
        newEl = document.createElement(newElement.element);

        if (newElement.className) {
            newEl.classList.add(newElement.className);
        }
    }

    if (!(newEl instanceof HTMLElement)) {
        return null;
    }

    const parentElement = referenceElement.parentNode;

    if (parentElement) {
        if (referenceElement.nextSibling) {
            parentElement.insertBefore(newEl, referenceElement.nextSibling);
        } else {
            parentElement.appendChild(newEl);
        }
    } else {
        return null;
    }

    return newEl;
}

/**
 * Check if an element is visible
 * @param {HTMLElement} element The element to check
 * @returns {boolean} True if the element is visible, false otherwise
 */
export default function isElementVisible(element) {
    const styles = window.getComputedStyle(element);

    return styles.getPropertyValue("display") !== "none";
}
